/** @format */
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { _$, Row, Col, Table, TableColumn, TableSortValue } from '@king-design/react'
import {
  getAgentKitRuntimeLLMOverview,
  getAgentKitRuntimeLLMMetric,
  getAgentKitRuntimeLLMModels
} from 'src/api/ApiAgentKit'
import { useAsync } from 'react-use'
import { AgentKitOverviewProps } from '../interface'
import { numberFormat } from 'src/utils/common'
import variable from 'src/static/styles/var'
import OverviewLineChart, {
  OverviewLineChartPoint
} from 'src/pages/AIObservability/components/OverviewLineChart'
import { fillMetricPoints } from 'src/pages/AIObservability/ServiceOverview/container/constant'
import { secondsFormat } from 'src/utils'
import { tokenValueFormat } from 'src/pages/AIObservability/constant'
import {
  MetricsSection,
  MetricCard,
  ChartsSection,
  ShareBar,
  ValueWithUnit,
  splitDurationSec
} from '../components'

const AgentKitLLM: React.FC<AgentKitOverviewProps> = ({
  className,
  serviceName,
  startTime,
  endTime,
  region,
  step
}) => {
  const [tableSort, setTableSort] = useState<TableSortValue>()

  const { value: metricData, loading: metricLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitRuntimeLLMOverview({
      StartTime: startTime,
      EndTime: endTime,
      ServiceName: serviceName
    })
    return result
  }, [region, startTime, endTime, serviceName])

  const avgDurationSec = useMemo(
    () => splitDurationSec(metricData?.AvgDurationSec),
    [metricData?.AvgDurationSec]
  )

  const errorCount = Number(metricData?.ErrorCount)
  const hasErrorCount = !Number.isNaN(errorCount) && errorCount > 0

  const { value: chartData, loading: chartLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitRuntimeLLMMetric({
      StartTime: startTime,
      EndTime: endTime,
      Step: `${step}s`,
      ServiceName: serviceName
    })
    return (result?.Series ?? []).reduce((acc, item) => {
      acc[item.Metric] = item.Points?.length
        ? fillMetricPoints(item.Points, startTime, endTime, step)
        : []
      return acc
    }, {} as Record<string, OverviewLineChartPoint[]>)
  }, [region, startTime, endTime, step, serviceName])

  const errorRate: OverviewLineChartPoint[] = useMemo(() => {
    return (
      chartData?.ErrorRate?.map(item => {
        return {
          ...item,
          Value: Number(numberFormat(item.Value * 100))
        }
      }) || []
    )
  }, [chartData?.ErrorRate])

  const { value: tableData, loading: tableLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitRuntimeLLMModels({
      StartTime: startTime,
      EndTime: endTime,
      ServiceName: serviceName
    })
    return result?.Models || []
  }, [region, startTime, endTime, serviceName])

  const loading = metricLoading || chartLoading || tableLoading

  return (
    <div className={className}>
      <MetricsSection loading={loading} aria-label={_$('指标')}>
        <MetricCard label={_$('模型调用总次数')} value={metricData?.InvocationCount ?? '-'} />
        <MetricCard
          label={_$('模型调用平均耗时')}
          value={<ValueWithUnit value={avgDurationSec.value} unit={avgDurationSec.unit} />}
        />
        <MetricCard
          label={_$('模型调用错误数')}
          value={metricData?.ErrorCount ?? '-'}
          hasError={hasErrorCount}
        />
        <MetricCard
          label={_$('模型调用错误率')}
          hasError={hasErrorCount}
          value={
            <ValueWithUnit
              value={
                metricData?.ErrorRate !== null ? numberFormat(metricData?.ErrorRate * 100) : '-'
              }
              unit="%"
            />
          }
        />
      </MetricsSection>
      <ChartsSection aria-label={_$('趋势图')}>
        <Row gutter="16">
          <Col span={12} className="overview-chart-col">
            <OverviewLineChart
              title={_$('调用次数')}
              data={chartData?.InvocationCount}
              colHeight={300}
            />
          </Col>
          <Col span={12} className="overview-chart-col">
            <OverviewLineChart
              title={_$('平均耗时P50')}
              data={chartData?.DurationP50}
              durationValue
              colHeight={300}
            />
          </Col>
          <Col span={12} className="overview-chart-col">
            <OverviewLineChart
              title={_$('耗时P95')}
              data={chartData?.DurationP95}
              durationValue
              colHeight={300}
            />
          </Col>
          <Col span={12} className="overview-chart-col">
            <OverviewLineChart
              title={_$('错误率')}
              data={errorRate}
              lineColor={variable.errorColor}
              yPercent
              colHeight={300}
            />
          </Col>
        </Row>
      </ChartsSection>

      <section className="overview-table" aria-label={_$('模型用量排行')}>
        <div className="service-list-header">
          <h2 className="llm-title">{_$('模型用量排行')}</h2>
        </div>
        <Table
          data={tableData || []}
          rowKey={data => data.Id}
          checkType="none"
          animation={false}
          loading={tableLoading}
          sort={tableSort}
          onChangeSort={sort => {
            setTableSort(sort)
          }}
        >
          <TableColumn key="ModelName" title={_$('模型名称')} />
          <TableColumn key="InvocationCount" title={_$('调用次数')} />
          <TableColumn
            key="TokenTotalUsage"
            title={_$('Token消耗数')}
            slotTemplate={([data]) => {
              return <span>{tokenValueFormat(data.TokenTotalUsage)}</span>
            }}
          />
          <TableColumn
            key="Share"
            sortable
            title={_$('占比')}
            slotTemplate={([data]) => <ShareBar share={data.Share} />}
          />
          <TableColumn
            key="AvgDurationSec"
            sortable
            title={_$('平均耗时')}
            slotTemplate={([data]) => {
              return <span>{secondsFormat(data.AvgDurationSec * 1000)}</span>
            }}
          />
          <TableColumn
            key="ErrorRate"
            title={_$('错误率')}
            slotTemplate={([data]) => {
              return (
                <span style={data.ErrorRate > 0 ? { color: variable.errorColor } : null}>
                  {numberFormat(data.ErrorRate * 100)}%
                </span>
              )
            }}
          />
        </Table>
      </section>
    </div>
  )
}

export default styled(AgentKitLLM)`
  .llm-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.85);
  }

  .overview-table {
    .service-list-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 16px 0;
    }
  }
`
